import { Account } from '@/iam';
import { CareProfile } from '@/careProfile';
import { Personalization, Relationship } from '@/onboarding';
import { ActionPlanItem } from '@/actionPlan';
import { QuestionResponse } from '@/triage';
import { formatFullName } from '@karehero/helpers/name';
import type { PaginationMeta } from '@/pagination';

export interface Role {
  id: string;
  name: string;
}

export interface CareCircleMember {
  id: string;
  account: Partial<Account>;
  careCircleID: string;
  isRegistered?: boolean;
  roles: Role[];
  careRecipientAccountRelationshipId: string;
  careRecipientAccountRelationship?: Relationship;
  personalization: Personalization[];
  careStageID: string;
  completedActions?: ActionPlanItem[];
  personalizedActions?: ActionPlanItem[];
}

export interface ActivityLogItem {
  id: string;
  careCircleId?: string | null;
  activityType: string;
  actorName: string;
  title: string;
  careRecipientName?: string;
  primaryCaregiverName?: string;
  occurredAt: string;
}

export interface CaseManagerSummary {
  accountId: string;
  firstName: string;
  lastName: string;
}

export interface CareCircle {
  id: string;
  careRecipientAccount: Account;
  primaryCaregiverAccount: Account;
  careCircleMembers: CareCircleMember[];
  careProfile?: CareProfile;
  caseManager?: CaseManagerSummary | null;
  watchers?: CaseManagerSummary[];
}

export const memberNameMap = (
  members: CareCircleMember[],
): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const m of members) {
    if (m.account?.id) {
      const name = formatFullName(m.account.firstName, m.account.lastName);
      if (name) map[m.account.id] = name;
    }
  }
  return map;
};

export interface CareCircleMemberInvite {
  careCircleId?: string;
  firstName?: string;
  email: string;
  careRecipientAccountRelationshipId: string;
}

/** Why an invite email was not sent, as the API reports it. */
export type InviteErrorCode =
  'already-registered' | 'invalid-email' | 'send-failed' | 'unknown';

export interface InviteError {
  message: string;
  code: InviteErrorCode;
}

export enum CareRecipientType {
  Self = 'self',
  Other = 'other',
  Stub = 'stub',
}

export interface CreateCareCircleSpec {
  primaryCaregiverAccountId: string;
  careRecipientType: CareRecipientType;
  careRecipientName?: string;
  triageResponses?: QuestionResponse[];
}

export interface PaginatedCareCircles {
  data: CareCircle[];
  pagination: PaginationMeta;
}
